/**
 * useSignupCTA Hook
 * 
 * Encapsulates the common logic for signup CTAs across the application.
 * Handles authentication checks, feature flags, and modal state for signup flows.
 * 
 * @returns {Object} Hook state and handlers
 * @returns {boolean} shouldShow - Whether the CTA should be displayed (false if user is authenticated)
 * @returns {boolean} isLoading - Whether feature flags are still loading
 * @returns {boolean} showComingSoonModal - Whether the coming soon modal should be displayed
 * @returns {Function} handleSignupClick - Handler for signup button clicks
 * @returns {Function} closeModal - Handler to close the coming soon modal
 */

import { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { useFeatureFlags } from '@/lib/useFeatureFlags';

export function useSignupCTA() {
  const { isAuthenticated, login } = useAuth();
  const { flags, loading: flagsLoading } = useFeatureFlags();
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);

  const handleSignupClick = () => {
    // Don't proceed if flags are still loading
    if (flagsLoading) {
      return;
    }
    
    if (flags.signUpAllowed) {
      login();
    } else {
      setShowComingSoonModal(true);
    }
  };

  const closeModal = () => {
    setShowComingSoonModal(false);
  };

  return {
    // Don't show CTA if user is already signed in
    shouldShow: !isAuthenticated,
    isLoading: flagsLoading,
    showComingSoonModal,
    handleSignupClick,
    closeModal,
  };
}
